(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];

  class MobileNavigation {
    constructor(root) {
      this.root = root;
      this.openButton = qs('[data-mobile-nav-open]');
      this.closeButtons = qsa('[data-mobile-nav-close]', root);
      if (!this.root || !this.openButton) return;
      this.openButton.addEventListener('click', () => this.open());
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.root.classList.contains('is-open')) this.close();
      });
    }
    open() {
      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');
      this.openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('lock-scroll');
      const focusTarget = qs('[data-mobile-nav-close]', this.root);
      if (focusTarget) focusTarget.focus();
    }
    close() {
      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');
      this.openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('lock-scroll');
      this.openButton.focus();
    }
  }

  class QuantityInput {
    constructor(root) {
      this.root = root;
      this.input = qs('input[type="number"]', root);
      if (!this.input) return;
      qsa('button', root).forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.quantityStep || 0);
          const current = Number(this.input.value || 1);
          const min = Number(this.input.min || 1);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          this.input.value = Math.min(max, Math.max(min, current + step));
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  class ProductForm {
    constructor(root) {
      this.root = root;
      this.form = qs('form[data-product-form]', root);
      this.variantInput = qs('[data-variant-id]', root);
      this.variantDataEl = qs('[data-product-variants]', root);
      this.price = qs('[data-product-price]', root);
      this.comparePrice = qs('[data-product-compare-price]', root);
      this.addButton = qs('[data-add-to-cart]', root);
      this.addButtonText = qs('[data-add-to-cart-text]', root);
      this.inventory = qs('[data-product-inventory]', root);
      this.sku = qs('[data-product-sku]', root);
      this.stickyPrice = qs('[data-sticky-price]');
      if (!this.form || !this.variantDataEl || !this.variantInput) return;
      try {
        this.variants = JSON.parse(this.variantDataEl.textContent);

        const preloadUrls = [
          ...new Set(
            this.variants
              .map((variant) => variant.featured_media_url)
              .filter(Boolean)
          )
        ];

        preloadUrls.forEach((url) => {
          const image = new Image();
          image.src = url;
        });
      } catch (error) {
        console.error('Mafia Moms: invalid product variant JSON', error);
        return;
      }
      qsa('[data-product-option]', root).forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
      this.onOptionChange(false);
    }
    selectedOptions() {
      return qsa('[data-option-group]', this.root).map((group) => {
        const checked = qs('[data-product-option]:checked', group);
        return checked ? checked.value : null;
      });
    }
    onOptionChange(updateUrl = true) {
      const selected = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
      this.updateVariant(variant, updateUrl);
    }
    updateVariant(variant, updateUrl) {
      if (!variant) {
        this.variantInput.value = '';
        this.setButton(false, this.root.dataset.unavailableText || 'Unavailable');
        return;
      }
      this.variantInput.value = variant.id;
      if (this.price) {
        this.price.textContent = variant.price_formatted;
        this.price.classList.toggle('am-product__sale-price', Boolean(variant.compare_at_price_formatted));
      }
      if (this.comparePrice) {
        this.comparePrice.textContent = variant.compare_at_price_formatted || '';
        this.comparePrice.hidden = !variant.compare_at_price_formatted;
      }
      if (this.stickyPrice) this.stickyPrice.textContent = variant.price_formatted;
      if (this.sku) this.sku.textContent = variant.sku || '—';
      if (this.inventory) {
        const dot = qs('.am-product__inventory-dot', this.inventory);
        const text = qs('[data-inventory-text]', this.inventory);
        if (variant.available) {
          if (dot) dot.className = 'am-product__inventory-dot';
          if (text) text.textContent = this.root.dataset.inStockText || 'In stock';
        } else {
          if (dot) dot.className = 'am-product__inventory-dot am-product__inventory-dot--out';
          if (text) text.textContent = this.root.dataset.soldOutText || 'Sold out';
        }
      }
      this.setButton(variant.available, variant.available ? (this.root.dataset.addText || 'Add to cart') : (this.root.dataset.soldOutText || 'Sold out'));
      if (updateUrl && history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
      if (variant.featured_media_id) {
        const media = qs(
          `[data-media-id="${variant.featured_media_id}"]`,
          this.root
        );

        const gallery = qs('.am-product__gallery', this.root);

        if (media && gallery) {
          qsa('.am-product__media', gallery).forEach((item) => {
            item.classList.toggle(
              'is-selected-variant-media',
              item === media
            );
          });

          if (window.matchMedia('(max-width: 800px)').matches) {
            media.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          } else {
            if (gallery.firstElementChild !== media) {
              gallery.insertBefore(
                media,
                gallery.firstElementChild
              );
            }

            
          }
        }
      }
    }
    setButton(enabled, text) {
      if (!this.addButton) return;
      this.addButton.disabled = !enabled;
      if (this.addButtonText) this.addButtonText.textContent = text;
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const recommendations = html.querySelector('product-recommendations');
          if (recommendations && recommendations.innerHTML.trim().length) this.innerHTML = recommendations.innerHTML;
        })
        .catch((error) => console.warn('Mafia Moms recommendations:', error));
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  const init = (context = document) => {
    const mobileNav = qs('[data-mobile-nav]', context === document ? document : context.closest('body') || document);
    if (mobileNav && !mobileNav.dataset.initialized) {
      mobileNav.dataset.initialized = 'true';
      new MobileNavigation(mobileNav);
    }
    qsa('[data-quantity]', context).forEach((root) => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      new QuantityInput(root);
    });
    qsa('[data-product-root]', context).forEach((root) => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      new ProductForm(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    init();
    qsa('[data-filter-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const facets = qs('[data-facets]');
        if (!facets) return;
        facets.classList.toggle('is-open');
        document.body.classList.toggle('lock-scroll', facets.classList.contains('is-open'));
      });
    });
    qsa('[data-filter-close]').forEach((button) => {
      button.addEventListener('click', () => {
        const facets = qs('[data-facets]');
        if (!facets) return;
        facets.classList.remove('is-open');
        document.body.classList.remove('lock-scroll');
      });
    });
    const stickyBuy = qs('[data-sticky-buy]');
    if (stickyBuy) stickyBuy.addEventListener('click', () => qs('[data-add-to-cart]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();


/* PRODUCT GALLERY V2 START */

(() => {

  const initGallery = (root) => {
    if (!root || root.dataset.galleryV2Ready === 'true') return;

    const stage = root.querySelector('[data-gallery-stage]');
    const thumbs = Array.from(
      root.querySelectorAll('[data-gallery-thumb]')
    );

    if (!stage) return;

    root.dataset.galleryV2Ready = 'true';


    const activateMedia = (mediaId) => {
      if (!mediaId) return;

      const mediaItems = Array.from(
        stage.querySelectorAll('[data-gallery-media]')
      );

      const target = mediaItems.find(
        (item) =>
          String(item.dataset.mediaId) === String(mediaId)
      );

      if (!target) return;


      mediaItems.forEach((item) => {
        const active = item === target;

        item.hidden = !active;
        item.classList.toggle('is-active', active);
        item.classList.toggle(
          'is-selected-variant-media',
          active
        );
      });


      thumbs.forEach((thumb) => {
        const active =
          String(thumb.dataset.targetMedia) ===
          String(mediaId);

        thumb.classList.toggle('is-active', active);
        thumb.setAttribute(
          'aria-pressed',
          active ? 'true' : 'false'
        );
      });


      const activeThumb = thumbs.find(
        (thumb) =>
          String(thumb.dataset.targetMedia) ===
          String(mediaId)
      );

      if (activeThumb) {
        activeThumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    };


    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        activateMedia(
          thumb.dataset.targetMedia
        );
      });
    });


    /*
     * Variant changes:
     * Wait one frame so the existing Mafia Moms
     * ProductController can update the hidden variant ID.
     */
    const productRoot =
      root.closest('[data-product-root]');

    if (productRoot) {

      productRoot
        .querySelectorAll('[data-product-option]')
        .forEach((input) => {

          input.addEventListener('change', () => {

            requestAnimationFrame(() => {

              const variantInput =
                productRoot.querySelector(
                  '[data-variant-id]'
                );

              const variantData =
                productRoot.querySelector(
                  '[data-product-variants]'
                );

              if (!variantInput || !variantData) return;

              let variants = [];

              try {
                variants =
                  JSON.parse(
                    variantData.textContent
                  );
              } catch (error) {
                return;
              }

              const selectedVariant =
                variants.find(
                  (variant) =>
                    String(variant.id) ===
                    String(variantInput.value)
                );

              if (
                selectedVariant &&
                selectedVariant.featured_media_id
              ) {
                activateMedia(
                  selectedVariant.featured_media_id
                );
              }

            });

          });

        });

    }


    /*
     * Ensure current selected variant is shown
     * when page loads with ?variant=
     */
    const currentSelected =
      stage.querySelector(
        '.is-selected-variant-media[data-media-id]'
      );

    if (currentSelected) {
      activateMedia(
        currentSelected.dataset.mediaId
      );
    }

  };


  const initAllProductGalleries = () => {
    document
      .querySelectorAll('[data-product-gallery]')
      .forEach(initGallery);
  };


  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      initAllProductGalleries
    );

  } else {

    initAllProductGalleries();

  }

})();

/* PRODUCT GALLERY V2 END */





/* CLEAN SIZE GUIDE V2 START */

(() => {

  const initSizeGuide = () => {

    document
      .querySelectorAll('[data-size-guide-link]')
      .forEach((button) => {

        button.addEventListener('click', () => {

          const root =
            button.closest('[data-product-root]') ||
            document;

          const detailsList =
            Array.from(
              root.querySelectorAll('details')
            );

          const target =
            detailsList.find((details) => {

              const summary =
                details.querySelector('summary');

              if (!summary) return false;

              const text =
                summary.textContent
                  .trim()
                  .toLowerCase();

              return (
                text.includes('fit') &&
                text.includes('size')
              );

            });

          if (!target) return;

          target.open = true;

          target.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

        });

      });

  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initSizeGuide
    );
  } else {
    initSizeGuide();
  }

})();

/* CLEAN SIZE GUIDE V2 END */
